import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  getClasses,
  softDeleteClass
} from "../services/APIClient";
import "./TeacherRemoveClassModal.css";

const OTHER_REASON = "Other reason";

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const REMOVE_CLASS_REASONS = [
  {
    value: "Added by mistake",
    detail: "The class should not have been added."
  },
  {
    value: "Duplicate class",
    detail: "The correct class already exists in my class list."
  },
  {
    value: "No longer needed",
    detail: "I do not need this class in my list anymore."
  },
  {
    value: OTHER_REASON,
    detail: "None of these fit."
  }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const getClassId = (classItem) => classItem?.id || classItem?._id;

const getGradeBooleanFromClass = (classItem) => {
  const gradeLevel = GRADE_LEVELS.find((grade) => classItem?.[grade.value]);
  return gradeLevel?.value || classItem?.gradeBoolean || "unknown";
};

const getGradeLabelFromClass = (classItem) => {
  const gradeBoolean = getGradeBooleanFromClass(classItem);
  const gradeLevel = GRADE_LEVELS.find((grade) => grade.value === gradeBoolean);
  return gradeLevel?.label || classItem?.grade || "";
};

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.academicYearLabel ||
  classItem?.yearLabel ||
  classItem?.schoolYear ||
  null;

const getStudentCountFromClass = (classItem) => {
  const possibleCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.pupilCount ??
    classItem?.students?.length;

  const numericCount = Number(possibleCount);
  return Number.isFinite(numericCount) ? numericCount : null;
};

const normaliseClass = (classItem) => {
  const grade = getGradeLabelFromClass(classItem);
  const academicYear = getAcademicYearFromClass(classItem);
  const studentCount = getStudentCountFromClass(classItem);
  const studentCopy =
    studentCount === null
      ? ""
      : `${studentCount} ${studentCount === 1 ? "student" : "students"}`;
  const detailLabel = [
    grade,
    academicYear,
    studentCopy
  ].filter(Boolean).join(", ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name: classItem?.name || "Unnamed Class",
    grade,
    academicYear,
    studentCount,
    detailLabel
  };
};

const getMatchingReason = (reason) => {
  const normalisedReason = normaliseComparableText(reason);

  return REMOVE_CLASS_REASONS.find(
    (reasonOption) => normaliseComparableText(reasonOption.value) === normalisedReason
  )?.value || "";
};

const normalisePrefill = (prefill) => {
  const className = normaliseText(prefill?.className || prefill?.class_name);
  const reason = normaliseText(prefill?.reason);
  const note = normaliseText(prefill?.note);
  const matchingReason = getMatchingReason(reason);

  if (!reason) {
    return {
      className,
      note,
      removeReason: "",
      otherReason: ""
    };
  }

  if (matchingReason === OTHER_REASON) {
    return {
      className,
      note: "",
      removeReason: OTHER_REASON,
      otherReason: note
    };
  }

  if (matchingReason) {
    return {
      className,
      note: "",
      removeReason: matchingReason,
      otherReason: ""
    };
  }

  return {
    className,
    note: "",
    removeReason: OTHER_REASON,
    otherReason: note || reason
  };
};

const getPrefilledClassMatch = (classes, className) => {
  if (!className) {
    return null;
  }

  const exactMatches = classes.filter(
    (classItem) => normaliseComparableText(classItem.name) === normaliseComparableText(className)
  );

  return exactMatches.length === 1 ? exactMatches[0] : null;
};

function TeacherRemoveClassModal({
  show,
  handleClose,
  schoolid,
  teacherid,
  prefill,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [searchTerm, setSearchTerm] = useState("");
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [removeNote, setRemoveNote] = useState("");
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState("");

  const trimmedSearchTerm = searchTerm.trim();
  const effectiveRemoveReason =
    removeReason === OTHER_REASON ? otherReason.trim() : removeReason;
  const canContinue =
    Boolean(selectedClass && effectiveRemoveReason && !isRemoving);

  useEffect(() => {
    if (!show) {
      return;
    }

    const initialPrefill = normalisePrefill(prefill);

    setStage("form");
    setSearchTerm(initialPrefill.className);
    setClasses([]);
    setSelectedClass(null);
    setRemoveReason(initialPrefill.removeReason);
    setOtherReason(initialPrefill.otherReason);
    setRemoveNote(initialPrefill.note);
    setError("");
    setIsLoadingClasses(true);

    getClasses()
      .then((classesResponse) => {
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map(normaliseClass)
          .filter((classItem) => classItem.id)
          .sort((classA, classB) => {
            if (classA.academicYear !== classB.academicYear) {
              return classB.academicYear?.localeCompare(classA.academicYear || "") || 0;
            }

            return classA.name.localeCompare(classB.name);
          });

        setClasses(normalisedClasses);
        const prefilledClass = getPrefilledClassMatch(
          normalisedClasses,
          initialPrefill.className
        );

        if (prefilledClass) {
          setSelectedClass(prefilledClass);
        }
      })
      .catch((classesError) => {
        console.error("Error loading teacher classes:", classesError);
        setClasses([]);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, prefill]);

  const filteredClasses = useMemo(() => {
    const normalisedSearchTerm = trimmedSearchTerm.toLowerCase();

    if (!normalisedSearchTerm) {
      return classes;
    }

    return classes.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear, classItem.detailLabel].some(
        (value) => String(value || "").toLowerCase().includes(normalisedSearchTerm)
      )
    );
  }, [classes, trimmedSearchTerm]);

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
    setError("");
  };

  const handleSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setSearchTerm(nextSearchTerm);
    setError("");

    if (
      selectedClass &&
      normaliseComparableText(nextSearchTerm) !== normaliseComparableText(selectedClass.name)
    ) {
      setSelectedClass(null);
    }
  };

  const handleReasonSelect = (reason) => {
    setRemoveReason(reason);
    setRemoveNote("");
    setError("");

    if (reason !== OTHER_REASON) {
      setOtherReason("");
    }
  };

  const handleNext = () => {
    if (!canContinue) {
      return;
    }

    setStage("confirm");
  };

  const removeClass = async () => {
    if (!canContinue || !schoolid) {
      setError("The class could not be removed. Please try again.");
      setStage("form");
      return;
    }

    setIsRemoving(true);
    setError("");

    try {
      const response = await softDeleteClass(schoolid, {
        classid: selectedClass.id,
        deletionReason: effectiveRemoveReason,
        teacherId: teacherid?.toString() || "unknown"
      });

      if (!response) {
        setError("The class could not be removed. Please try again.");
        setStage("form");
        return;
      }

      setClasses((existingClasses) =>
        existingClasses.filter((classItem) => classItem.id !== selectedClass.id)
      );
      setStage("success");
    } catch (removeError) {
      console.error("Error removing teacher class:", removeError);
      setError("The class could not be removed. Please try again.");
      setStage("form");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleReturnToClasses = () => {
    setStage("form");
    setSearchTerm("");
    setSelectedClass(null);
    setRemoveReason("");
    setOtherReason("");
    setRemoveNote("");
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        message: "Class removed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="remove-class-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (selectedClass) {
      return (
        <button
          key={selectedClass.id}
          type="button"
          className="remove-class-result selected"
          onClick={() => setSelectedClass(null)}
          aria-label={`Select ${selectedClass.name}${selectedClass.detailLabel ? ` - ${selectedClass.detailLabel}` : ""}`}
          aria-pressed
        >
          <span className="remove-class-radio" aria-hidden="true" />
          <span className="remove-class-result-copy">
            <strong>{selectedClass.name}</strong>
            {selectedClass.detailLabel && (
              <span>{selectedClass.detailLabel}</span>
            )}
          </span>
        </button>
      );
    }

    if (filteredClasses.length < 1) {
      return (
        <p className="remove-class-muted">
          No matching classes found in your class list.
        </p>
      );
    }

    return filteredClasses.map((classItem) => {
      const isSelected = selectedClass?.id === classItem.id;

      return (
        <button
          key={classItem.id}
          type="button"
          className={`remove-class-result${isSelected ? " selected" : ""}`}
          onClick={() => handleClassSelect(classItem)}
          aria-label={`Select ${classItem.name}${classItem.detailLabel ? ` - ${classItem.detailLabel}` : ""}`}
          aria-pressed={isSelected}
        >
          <span className="remove-class-radio" aria-hidden="true" />
          <span className="remove-class-result-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && (
              <span>{classItem.detailLabel}</span>
            )}
          </span>
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Remove Class</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-class-intro">Choose the class you want to remove.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="removeClassSearch">
          <Form.Label>Which class are you removing?</Form.Label>
          <Form.Text as="div" className="remove-class-field-label">
            Search class
          </Form.Text>
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Start typing a class name"
            aria-label="Search class"
          />
          <Form.Text className="text-muted">
            Start typing a class name.
          </Form.Text>
        </Form.Group>

        <div className="remove-class-results" aria-label="Matching classes">
          {renderClassResults()}
        </div>

        <Form.Group className="mt-4" controlId="removeClassReason">
          <Form.Label>Why are you removing this class?</Form.Label>
          <div className="remove-class-reasons">
            {REMOVE_CLASS_REASONS.map((reason) => (
              <label
                key={reason.value}
                className={`remove-class-reason${removeReason === reason.value ? " selected" : ""}`}
              >
                <Form.Check
                  type="radio"
                  name="removeClassReason"
                  checked={removeReason === reason.value}
                  onChange={() => handleReasonSelect(reason.value)}
                  aria-label={reason.value}
                />
                <span className="remove-class-reason-copy">
                  <strong>{reason.value}</strong>
                  <span>{reason.detail}</span>
                </span>
              </label>
            ))}
          </div>
          {removeReason === OTHER_REASON && (
            <Form.Group className="remove-class-other-reason" controlId="removeClassOtherReason">
              <Form.Label>Other reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={otherReason}
                onChange={(event) => setOtherReason(event.target.value)}
                placeholder="Tell us why this class should be removed"
                aria-label="Other remove class reason"
              />
            </Form.Group>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-class-footer-note">
          {selectedClass ? `${selectedClass.name} selected.` : ""}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canContinue} onClick={handleNext}>
          Next
        </Button>
      </Modal.Footer>
    </>
  );

  const renderConfirmation = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Remove Class</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="remove-class-confirm-panel">
          <h3>Remove {selectedClass?.name}?</h3>
          <div className="remove-class-confirm-row">
            <strong>Selected class</strong>
            <span>
              {[selectedClass?.name, selectedClass?.detailLabel].filter(Boolean).join(", ")}
            </span>
          </div>
          <div className="remove-class-confirm-row">
            <strong>Reason</strong>
            <span>{effectiveRemoveReason}</span>
          </div>
          {removeNote && (
            <div className="remove-class-confirm-row">
              <strong>Note</strong>
              <span>{removeNote}</span>
            </div>
          )}
          <div className="remove-class-confirm-row">
            <strong>What changes</strong>
            <span>{selectedClass?.name} will no longer appear in your class list.</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button variant="danger" disabled={isRemoving} onClick={removeClass}>
          {isRemoving ? "Removing..." : "Remove class"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Class Removed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-class-intro">
          {selectedClass?.name} is no longer in your class list.
        </p>
        <Alert variant="success" className="remove-class-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{selectedClass?.name} has been removed.</strong>
            <span>You will not see it in your class list anymore.</span>
          </span>
        </Alert>
        <div className="remove-class-next">
          <strong>What next?</strong>
          <span>{embedded ? "Done returns to SENSAI." : "Done returns to Help Centre home."}</span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleReturnToClasses}>
          Return to classes
        </Button>
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-remove-class-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "confirm" && renderConfirmation()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherRemoveClassModal;
